import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, useMediaQuery } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { IncomeStatisticsResponse, IncomeBreakdownResponse } from '../../types/statistics';
import { useStatisticsApi } from '../../hooks/useStatisticsApi';

interface IncomeBarChartProps {
  incomeData: IncomeStatisticsResponse;
}

const IncomeBarChart: React.FC<IncomeBarChartProps> = ({ incomeData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fetchIncomeStatisticsBreakdown } = useStatisticsApi();
  const [breakdownData, setBreakdownData] = useState<IncomeBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastFetchKey, setLastFetchKey] = useState<string>('');

  useEffect(() => {
    const fetchKey = `${incomeData.period.start}_${incomeData.period.end}`;
    
    // Skip if we're already fetching/loaded this exact period
    if (fetchKey === lastFetchKey && breakdownData !== null) {
      return;
    }

    let isMounted = true;
    
    const loadBreakdownData = async () => {
      setLoading(true);
      setLastFetchKey(fetchKey);
      
      try {
        const breakdown = await fetchIncomeStatisticsBreakdown({
          startDate: incomeData.period.start,
          endDate: incomeData.period.end,
        });
        if (isMounted) {
          setBreakdownData(breakdown);
        }
      } catch (error) {
        console.error('Error loading breakdown data:', error);
        if (isMounted) {
          setBreakdownData(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBreakdownData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeData.period.start, incomeData.period.end]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  const chartHeight = isMobile ? 300 : 400;
  const chartMargin = isMobile 
    ? { top: 20, right: 10, bottom: 60, left: 40 }
    : { top: 20, right: 20, bottom: 40, left: 20 };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('statistics.incomeOverview')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Check if there's no data or all values are zero
  const hasData = breakdownData && breakdownData.breakdown.length > 0 && 
    breakdownData.breakdown.some(item => 
      item.finalizedAppointments > 0 || 
      item.soldProducts > 0 || 
      item.predictedIncome > 0
    );

  if (!hasData) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('statistics.incomeOverview')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
            <Typography variant="body1" color="text.secondary">
              {t('statistics.noData')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const labels = breakdownData!.breakdown.map((item) => item.label);
  const finalizedData = breakdownData!.breakdown.map((item) => item.finalizedAppointments);
  const soldProductsData = breakdownData!.breakdown.map((item) => item.soldProducts);
  const predictedIncomeData = breakdownData!.breakdown.map((item) => item.predictedIncome);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('statistics.incomeOverview')}
        </Typography>
        <Box sx={{ width: '100%', height: isMobile ? 'auto' : chartHeight, marginLeft: { xs: 0, md: -2 }, overflowX: 'auto' }}>
          <BarChart
            xAxis={[
              {
                scaleType: 'band',
                data: labels,
              },
            ]}
            yAxis={[
              {
                label: 'PLN',
              },
            ]}
            series={[
              {
                data: finalizedData,
                label: t('statistics.finalizedAppointments'),
                color: '#2e7d32', // Green - same as completed appointments in calendar
                valueFormatter: (value) => value != null ? formatCurrency(value) : '',
                stack: 'total',
              },
              {
                data: soldProductsData,
                label: t('statistics.soldProducts'),
                color: theme.palette.secondary.main,
                valueFormatter: (value) => value != null ? formatCurrency(value) : '',
                stack: 'total',
              },
              {
                data: predictedIncomeData,
                label: t('statistics.plannedAppointments'),
                color: '#1976d2', // Blue - same as planned appointments in calendar
                valueFormatter: (value) => value != null ? formatCurrency(value) : '',
                stack: 'total',
              },
            ]}
            height={chartHeight}
            margin={chartMargin}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default IncomeBarChart;

